import { PARENT_URL } from 'astro:env/client';
import type { JSX } from 'preact';
import { INTERACTIONS } from '@/analytics/tracking/events.config';
import type { Image } from '@/core/data/schemas/asset.schema';
import { compactMap } from '@/core/utils/array';
import { DEFAULT_LOCALE } from '@/core/utils/time.client';
import type { City } from '@/events/cities/city.schema';
import {
	ERROR_TEXT,
	EVENT_SECTION_TITLE,
	NO_RESULTS_TEXT,
	PERFORMER_SECTION_TITLE,
	VENUE_SECTION_TITLE,
} from '../search.config';
import type { SearchResult, SearchResults } from '../search.schema';
import {
	transformEventToSearchResult,
	transformPerformerToSearchResult,
	transformVenueToSearchResult,
} from '../search.transformer.client';

type RenderContext = Readonly<{
	apiSequence: number;
	inputValue: string;
	keystrokeSequence: number;
	location?: City;
	sessionId: string;
	status: 'idle' | 'loading' | 'ready' | 'error';
	results: SearchResults;
}>;

function buildViewMoreHref({
	apiSequence,
	keystrokeSequence,
	location,
	query,
	sessionId,
}: Readonly<{
	apiSequence: number;
	keystrokeSequence: number;
	location?: City;
	query: string;
	sessionId: string;
}>): string {
	const url = new URL('search', PARENT_URL);
	url.searchParams.set('q', query);

	url.searchParams.set(
		'd',
		buildSearchId({ apiSequence, keystrokeSequence, sessionId }),
	);

	if (location?.location.coordinates?.lat !== undefined) {
		url.searchParams.set('lat', location?.location.coordinates.lat.toString());
	}

	if (location?.location.coordinates?.lon !== undefined) {
		url.searchParams.set('long', location?.location.coordinates.lon.toString());
	}

	return url.toString();
}

function buildSearchId({
	apiSequence,
	keystrokeSequence,
	sessionId,
}: Readonly<{
	apiSequence: number;
	keystrokeSequence: number;
	sessionId: string;
}>): string {
	return btoa(
		JSON.stringify({
			apiSequence,
			keystrokeSequence,
			searchSessionId: sessionId,
		}),
	);
}

function renderSection({
	items,
	title,
}: Readonly<{
	items: SearchResult[];
	title: string;
}>) {
	if (items.length === 0) return null;

	return (
		<li class="dropdown-section">
			<h2 class="dropdown-header">{title}</h2>

			<ul class="dropdown-section-list unlist">
				{items.map((item) => (
					<li key={item.url}>
						<a
							class="dropdown-list-item"
							data-analytics-interaction={INTERACTIONS.searchBar}
							href={item.url}
						>
							{item.assets?.thumbnail ? (
								<ImageIcon image={item.assets.thumbnail} />
							) : null}

							<DateIcon date={item.date} />

							<div class="dropdown-list-item-text-container">
								<p class="dropdown-list-item-title">{item.title}</p>

								<p class="dropdown-list-item-subtitle">
									{item.price ? (
										<span class="dropdown-list-item-price">{item.price}</span>
									) : null}

									{item.subtitle}
								</p>
							</div>
						</a>
					</li>
				))}
			</ul>
		</li>
	);
}

function ImageIcon({ image }: Readonly<{ image: Image }>) {
	return (
		<figure class="featured-image natural dropdown-list-item-icon">
			<img
				alt={image.alt}
				height={image.height}
				loading="lazy"
				src={image.src}
				width={image.width}
			/>
		</figure>
	);
}

function DateIcon({ date }: Readonly<{ date?: string | Date }>) {
	if (!date) return null;

	const fullDate = new Date(date);

	if (Number.isNaN(fullDate.getTime())) return null;

	return (
		<time class="dropdown-list-item-icon" dateTime={fullDate.toISOString()}>
			<p class="dropdown-list-item-title">
				{fullDate.toLocaleDateString(DEFAULT_LOCALE, {
					day: 'numeric',
					month: 'numeric',
				})}
			</p>

			<p class="dropdown-list-item-subtitle">
				{fullDate.toLocaleDateString(DEFAULT_LOCALE, {
					weekday: 'short',
				})}
			</p>
		</time>
	);
}

export function SearchResultsView({
	apiSequence,
	inputValue,
	keystrokeSequence,
	location,
	results,
	sessionId,
	status,
}: RenderContext): JSX.Element {
	const transformed = {
		events: compactMap(results.events, transformEventToSearchResult),
		performers: compactMap(
			results.performers,
			transformPerformerToSearchResult,
		),
		venues: compactMap(results.venues, transformVenueToSearchResult),
	};

	const hasResults =
		transformed.events.length > 0 ||
		transformed.performers.length > 0 ||
		transformed.venues.length > 0;

	const viewMoreHref =
		status === 'ready' && inputValue
			? buildViewMoreHref({
					apiSequence,
					keystrokeSequence,
					location,
					query: inputValue,
					sessionId,
				})
			: undefined;

	if (status === 'error') {
		return <li class="dropdown-list-item">{ERROR_TEXT}</li>;
	}

	return (
		<>
			{renderSection({
				items: transformed.events,
				title: EVENT_SECTION_TITLE,
			})}

			{renderSection({
				items: transformed.performers,
				title: PERFORMER_SECTION_TITLE,
			})}

			{renderSection({
				items: transformed.venues,
				title: VENUE_SECTION_TITLE,
			})}

			{status === 'ready' && !hasResults ? (
				<li class="dropdown-list-item no-results">{NO_RESULTS_TEXT}</li>
			) : null}

			{viewMoreHref ? (
				<li>
					<a class="dropdown-list-item learn-more-link" href={viewMoreHref}>
						<span>View More Results</span>
						<span class="icon">^</span>
					</a>
				</li>
			) : null}
		</>
	);
}
